import { useMemo, useRef, useState } from 'react';
import { useCommissionSummary } from '../../application/hooks/useCommissions';
import { useTranslation } from '../../application/hooks/useTranslation';
import {
  usePayoutOptions,
  useWithdrawalHistory,
  useWithdrawalRequest,
} from '../../application/hooks/useWithdrawals';
import { resolveWallet } from '../../domain/calculators/processWithdrawal';
import { quoteWithdrawal, MIN_WITHDRAWAL } from '../../domain/calculators/withdrawalFee';
import { formatBDT } from '../../domain/formatters/currency';
import { formatDateTime } from '../../domain/formatters/date';
import { IdempotencyKeyHolder } from '../../domain/withdrawals/idempotencyKey';
import { parseWithdrawalError } from '../../domain/withdrawals/withdrawalErrors';
import type { WithdrawalMethod } from '../../domain/contracts/api.contracts';
import { QueryState } from '../components/feedback/QueryState';
import { withdrawalErrorText } from '../components/withdrawals/withdrawalMessages';
import { AmountDisplay } from '../components/ui/AmountDisplay';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import './pages.css';
import './Withdrawals.css';
import { WITHDRAWALS_ENABLED } from '../../config/features';

/** The backend kill-switch answers new requests with 503 WITHDRAWALS_DISABLED; the flag mirrors it ahead of time. */
function isServiceUnavailable(err: unknown): boolean {
  const info = parseWithdrawalError(err);
  return info.code === 'WITHDRAWALS_DISABLED' || info.status === 503;
}

const STATUS_TONE: Record<string, 'warning' | 'info' | 'success' | 'neutral'> = {
  PENDING_VERIFICATION: 'warning',
  PENDING_ADMIN: 'warning',
  AUTHORIZED: 'info',
  DISBURSED: 'success',
  REJECTED: 'neutral',
  EXPIRED: 'neutral',
};

/**
 * W10 — Commission Withdrawal Hub (BD-31): bKash or bank transfer, paid to the member's saved payout details.
 * The member only picks an amount and a rail; admin approves and sends the money (no OTP, no cash counter).
 * Commission-as-installment is retired (BD-31): agents own no subscriptions, so commission is only withdrawn.
 */
export default function WithdrawalsPage() {
  const { t, locale } = useTranslation();
  const summary = useCommissionSummary();
  const history = useWithdrawalHistory({ page: 1, limit: 10 });
  const payout = usePayoutOptions();
  const request = useWithdrawalRequest();

  // Duplicate-submit protection: a synchronous in-flight latch (state updates are async, so a fast double click
  // could otherwise slip past `isPending`) plus one stable Idempotency-Key per distinct form content.
  const inFlight = useRef(false);
  const keys = useRef(new IdempotencyKeyHolder());

  const [amount, setAmount] = useState('');
  const [choice, setChoice] = useState<WithdrawalMethod | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const paused = !WITHDRAWALS_ENABLED || isServiceUnavailable(request.error);

  const available = resolveWallet(summary.data).availableForWithdrawal;
  const options = payout.data?.options ?? [];
  // A lone rail is preselected; with several, the member must choose.
  const method: WithdrawalMethod | null =
    choice && options.some((o) => o.method === choice) ? choice : options.length === 1 ? options[0].method : null;
  const noPayoutAccount = !payout.isPending && !payout.error && options.length === 0;

  const quote = useMemo(() => quoteWithdrawal(Number(amount), available), [amount, available]);

  const methodLabel = (m: string) =>
    m === 'BKASH' ? t('withdrawals.railBkash') : m === 'BANK_EFT' ? t('withdrawals.railBank') : t('withdrawals.methodLegacy');

  const submitRequest = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmitted(false);
    if (paused || !method) return;
    if (!quote.ok) {
      setFormError(
        quote.reason === 'below-minimum'
          ? t('withdrawals.minAmount', { min: formatBDT(MIN_WITHDRAWAL, locale) })
          : quote.reason === 'exceeds-balance'
            ? t('withdrawals.errInsufficient')
            : t('common.error'),
      );
      return;
    }
    if (inFlight.current || request.isPending) return;
    inFlight.current = true;
    const idempotencyKey = keys.current.keyFor(JSON.stringify([method, Number(amount)]));
    request.mutate(
      { body: { amount: Number(amount), method }, idempotencyKey },
      {
        onSuccess: () => {
          keys.current.reset();
          setAmount('');
          setSubmitted(true);
        },
        onError: (err) => setFormError(withdrawalErrorText(err, t)),
        onSettled: () => {
          inFlight.current = false;
        },
      },
    );
  };

  return (
    <div data-testid="withdrawals-page" className="stack" style={{ gap: 'var(--space-6)' }}>
      <div className="page-head">
        <h1 className="page-title">{t('withdrawals.title')}</h1>
      </div>

      <div className="grid-2">
        <Card title={t('withdrawals.request')}>
          <form className="stack form-narrow" onSubmit={submitRequest} noValidate>
            {paused && (
              <div className="wd-paused-banner" role="status" data-testid="wd-paused">
                <strong>{t('withdrawals.pausedTitle')}</strong>
                <p>{t('withdrawals.pausedBody')}</p>
              </div>
            )}
            <div>
              <div className="stat-label">{t('commissions.available')}</div>
              <div className="stat-value" data-testid="wd-available">
                <AmountDisplay amount={available} />
              </div>
            </div>

            <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="field-label">{t('withdrawals.payoutTo')}</legend>
              <QueryState
                loading={payout.isPending}
                error={payout.error}
                onRetry={() => void payout.refetch()}
                skeletonCount={2}
              />
              {noPayoutAccount && (
                <p className="muted" role="status" data-testid="wd-no-payout">
                  {t('withdrawals.noPayoutAccount')}
                </p>
              )}
              {options.map((o) => (
                <label
                  key={o.method}
                  className={`wd-option${method === o.method ? ' wd-option-selected' : ''}`}
                  data-testid={`payout-option-${o.method}`}
                >
                  <input
                    type="radio"
                    name="payout-method"
                    value={o.method}
                    checked={method === o.method}
                    onChange={() => setChoice(o.method)}
                  />
                  <span className="wd-option-body">
                    <strong>{methodLabel(o.method)}</strong>
                    {o.bankName && <span data-testid="payout-bank-name">{o.bankName}</span>}
                    <span className="muted" data-testid="payout-account">
                      {o.accountMasked}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>

            <Input
              label={`${t('installments.amount')} (BDT)`}
              type="number"
              min={MIN_WITHDRAWAL}
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setSubmitted(false);
              }}
              hint={`${t('withdrawals.quoteHint')} ${t('withdrawals.minAmount', { min: formatBDT(MIN_WITHDRAWAL, locale) })}`}
              error={amount && !quote.ok ? (quote.reason === 'exceeds-balance' ? t('withdrawals.errInsufficient') : t('withdrawals.minAmount', { min: formatBDT(MIN_WITHDRAWAL, locale) })) : null}
              data-testid="wd-amount"
            />
            {quote.ok && (
              <dl className="kv" data-testid="wd-quote">
                <dt>{t('withdrawals.payable')}</dt>
                <dd>
                  <AmountDisplay amount={quote.quote.netAmount} bold />
                  <span
                    style={{
                      display: 'block',
                      fontSize: '0.6875rem',
                      color: 'var(--color-text-muted)',
                      fontWeight: 'normal',
                      marginTop: '2px',
                    }}
                    data-testid="service-charge-label"
                  >
                    {t('withdrawals.serviceChargeNote')}
                  </span>
                </dd>
              </dl>
            )}

            {formError && (
              <p className="text-danger" role="alert" data-testid="wd-error">
                {formError}
              </p>
            )}
            {submitted && (
              <p role="status" data-testid="wd-submitted">
                <Badge tone="warning">{t('withdrawals.status.PENDING_ADMIN')}</Badge> {t('withdrawals.submittedNote')}
              </p>
            )}
            <Button
              type="submit"
              loading={request.isPending}
              disabled={paused || !quote.ok || !method}
              data-testid="wd-submit"
            >
              {t('withdrawals.request')}
            </Button>
          </form>
        </Card>

        <Card title={t('withdrawals.history')}>
          <QueryState
            loading={history.isPending}
            error={history.error}
            empty={!history.isPending && (history.data?.items.length ?? 0) === 0}
            onRetry={() => void history.refetch()}
            skeletonCount={4}
          />
          {history.data && history.data.items.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>{t('invoice.date')}</th>
                    <th>{t('payments.channel')}</th>
                    <th>{t('withdrawals.fee')}</th>
                    <th>{t('withdrawals.net')}</th>
                    <th>{t('installments.status')}</th>
                    <th>{t('withdrawals.transactionId')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.data.items.map((r) => (
                    <tr key={r.id} data-testid="wd-row">
                      <td>{formatDateTime(r.requestedAt, locale)}</td>
                      <td>{methodLabel(r.payoutMethod)}</td>
                      <td data-testid="wd-fee">
                        {r.feeAmount > 0 ? <AmountDisplay amount={r.feeAmount} negative /> : <span className="muted">—</span>}
                      </td>
                      <td>
                        <AmountDisplay amount={r.netAmount} />
                      </td>
                      <td>
                        <Badge tone={STATUS_TONE[r.status] ?? 'neutral'}>{t(`withdrawals.status.${r.status}`)}</Badge>
                      </td>
                      <td data-testid="wd-txn">
                        {r.transactionId ? <code>{r.transactionId}</code> : <span className="muted">{t('withdrawals.notYetSent')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
